import {
  isFiniteVerb,
  SUBJECT_PRONOUNS,
} from "@/lib/tool-kit/style-checkers/a-lexicon";
import { type Word, wordsIn } from "@/lib/tool-kit/style-checkers/a-words";
import { countWords, type Span } from "@/lib/tool-kit/writing-analysis/text";

/**
 * Dialogue punctuation, American convention (The Chicago Manual of Style; Purdue OWL gives
 * the same rules for commas and tags):
 *
 * - Before a tag the quote ends with a comma, not a period: "I'm leaving," she said.
 *   After ? or ! no comma is added, and the tag stays lowercase: "Are you leaving?" she asked.
 * - A tag that comes first is followed by a comma: She said, "I'm leaving."
 * - Periods and commas go inside the closing quotation mark.
 * - Speech that opens a sentence starts with a capital; the second half of a quote broken
 *   by a tag ("I'm leaving," she said, "when it rains.") starts lowercase.
 * - An action is not a tag: "I'm leaving." She grabbed her coat. (period), not
 *   "I'm leaving," she grabbed her coat.
 *
 * Quotes are read line by line, so a line break ends a paragraph. A paragraph with an
 * opening mark and no closing one is reported unless the next paragraph opens with a mark
 * (a speech that runs over several paragraphs). Single-quote dialogue is not read.
 */

export type DialogueRule =
  | "period-before-tag"
  | "missing-comma-after-tag"
  | "comma-after-question"
  | "mark-placement"
  | "capital-tag"
  | "action-beat"
  | "lowercase-start"
  | "capital-continuation"
  | "unclosed";

export interface DialogueFinding extends Span {
  rule: DialogueRule;
  /** The stretch of your text the fix replaces, with the quote mark and a word of context. */
  written: string;
  /** What to put there, or null when the finding needs a decision rather than an edit. */
  fixed: string | null;
  /** True for rules the manuals state outright; false for judgement calls. */
  firm: boolean;
  /** Line number, from 1. */
  line: number;
  lineText: string;
}

export interface DialogueReport {
  words: number;
  lines: number;
  quotes: number;
  findings: DialogueFinding[];
  byRule: Record<DialogueRule, number>;
}

/* ------------------------------------------------------------------ word lists */

const SPEECH_VERBS: ReadonlySet<string> = new Set([
  "said",
  "says",
  "asked",
  "asks",
  "replied",
  "replies",
  "answered",
  "answers",
  "whispered",
  "shouted",
  "yelled",
  "cried",
  "called",
  "muttered",
  "murmured",
  "exclaimed",
  "added",
  "continued",
  "explained",
  "suggested",
  "demanded",
  "announced",
  "declared",
  "insisted",
  "breathed",
  "snapped",
  "barked",
  "retorted",
  "responded",
  "remarked",
  "observed",
  "noted",
  "wondered",
  "stammered",
  "stuttered",
  "mumbled",
  "growled",
  "hissed",
  "pleaded",
  "begged",
  "urged",
  "warned",
  "whined",
  "snarled",
  "told",
  "inquired",
  "queried",
  "stated",
  "interrupted",
  "cheered",
  "protested",
  "recalled",
  "admitted",
  "agreed",
  "argued",
  "bellowed",
  "blurted",
  "gasped",
  "grumbled",
  "announces",
  "shouts",
  "whispers",
  "replied",
  "thought",
  "offered",
  "conceded",
  "coaxed",
  "joked",
  "teased",
  "sobbed",
  "screamed",
  "squealed",
  "roared",
  "boomed",
  "prompted",
]);

/** Capitalised words that open a tag when a quote ending in , ? or ! has been capitalised wrongly after it. */
const TAG_OPENERS: ReadonlySet<string> = new Set([
  "he",
  "she",
  "they",
  "we",
  "it",
  "you",
  "the",
  "a",
  "an",
  "his",
  "her",
  "their",
  "my",
  "our",
  "your",
  "its",
  "one",
  "someone",
  "everyone",
]);

/** Function words that begin the second half of a broken quote, never a proper noun. */
const CONTINUATION_WORDS: ReadonlySet<string> = new Set([
  "the",
  "a",
  "an",
  "and",
  "but",
  "or",
  "so",
  "when",
  "if",
  "because",
  "that",
  "then",
  "to",
  "of",
  "in",
  "on",
  "at",
  "we",
  "you",
  "he",
  "she",
  "it",
  "they",
  "this",
  "there",
  "what",
  "why",
  "how",
  "who",
  "which",
  "as",
  "while",
  "until",
  "unless",
  "since",
  "for",
  "with",
  "not",
  "no",
  "would",
  "could",
  "can",
  "should",
  "must",
  "may",
  "might",
  "will",
  "is",
  "are",
  "was",
  "were",
  "has",
  "have",
  "had",
  "do",
  "does",
  "did",
]);

/* ------------------------------------------------------------------ quotes */

interface Quote {
  open: number;
  /** Offset of the closing mark, or -1 when the line ends first. */
  close: number;
  line: number;
}

const RE_LINE = /[^\n]+/g;
const OPENING = "“";
const CLOSING = "”";
const STRAIGHT = '"';

/** Pairs the double quotation marks on each line; a straight mark alternates open/close. */
function quotesOnLine(
  text: string,
  lineStart: number,
  lineEnd: number,
  line: number
): Quote[] {
  const quotes: Quote[] = [];
  let open = -1;
  for (let i = lineStart; i < lineEnd; i++) {
    const char = text[i];
    const opens = char === OPENING || (char === STRAIGHT && open === -1);
    if (opens && open === -1) {
      open = i;
    } else if ((char === CLOSING || char === STRAIGHT) && open !== -1) {
      quotes.push({ open, close: i, line });
      open = -1;
    }
  }
  if (open !== -1) {
    quotes.push({ open, close: -1, line });
  }
  return quotes;
}

function allQuotes(text: string): { quotes: Quote[]; lines: Span[] } {
  const quotes: Quote[] = [];
  const lines: Span[] = [];
  for (const match of text.matchAll(RE_LINE)) {
    const span = { start: match.index, end: match.index + match[0].length };
    lines.push(span);
    quotes.push(...quotesOnLine(text, span.start, span.end, lines.length));
  }
  return { quotes, lines };
}

/* ------------------------------------------------------------------ helpers */

const RE_SPACES = /^\s*/;
const RE_TRAILING_SPACE = /\s+$/;
const RE_ELLIPSIS_AFTER = /^\.{2}/;
const RE_LOWER = /^\p{Ll}/u;
const RE_UPPER = /^\p{Lu}/u;

function lastInner(
  text: string,
  quote: Quote
): { char: string; index: number } | null {
  const inner = text.slice(quote.open + 1, quote.close);
  const trimmed = inner.replace(RE_TRAILING_SPACE, "");
  if (trimmed.length === 0) {
    return null;
  }
  const index = quote.open + trimmed.length;
  return { char: trimmed.at(-1) ?? "", index };
}

interface Tag {
  /** Start of the first word after the closing mark. */
  firstStart: number;
  firstEnd: number;
  first: string;
  /** The speech verb found, or null when the following words are not a tag. */
  verb: string | null;
}

const RE_TAG_BREAK = /[.,;:!?—–"“”]/;
const RE_TAG_END = /^\s*(?:$|[.,;:!?—–])/;
const RE_LY = /^\p{L}{3,}ly$/u;

/** Words that may sit between a speech verb and the end of the tag: "said quietly", "said to her". */
const TAG_TAIL_WORDS: ReadonlySet<string> = new Set([
  "aloud",
  "again",
  "back",
  "then",
  "to",
  "at",
  "him",
  "her",
  "them",
  "me",
  "us",
  "you",
  "softly",
]);

/**
 * Whether the words after the verb finish the tag: the tag ends at the next punctuation or
 * the end of the line, after at most a few adverbs ("said quietly"), or a short subject when
 * the verb comes first ("said Tom"). "It says do not hire" is a sentence, not a tag.
 */
function tagEnds(
  text: string,
  words: readonly Word[],
  verbAt: number,
  lineEnd: number
): boolean {
  const verb = words[verbAt];
  const tail = words.slice(verbAt + 1, verbAt + 4);
  let last = verb;
  for (const word of tail) {
    if (RE_TAG_BREAK.test(text.slice(last.end, word.start))) {
      break;
    }
    if (
      !(
        verbAt === 0 ||
        RE_LY.test(word.lower) ||
        TAG_TAIL_WORDS.has(word.lower)
      )
    ) {
      return false;
    }
    last = word;
  }
  return RE_TAG_END.test(text.slice(last.end, lineEnd));
}

/** The words after a closing mark and whether they make a dialogue tag. */
function tagAfter(text: string, quote: Quote, lineEnd: number): Tag | null {
  const from = quote.close + 1;
  const gap = RE_SPACES.exec(text.slice(from, lineEnd))?.[0].length ?? 0;
  const start = from + gap;
  const words = wordsIn(text, start, Math.min(lineEnd, start + 90));
  if (words.length === 0 || words[0].start !== start) {
    return null;
  }
  let verb: string | null = null;
  for (const [i, word] of words.slice(0, 5).entries()) {
    const between = text.slice(i === 0 ? start : words[i - 1].end, word.start);
    if (RE_TAG_BREAK.test(between)) {
      break;
    }
    if (SPEECH_VERBS.has(word.lower)) {
      verb = tagEnds(text, words, i, lineEnd) ? word.lower : null;
      break;
    }
  }
  return {
    firstStart: words[0].start,
    firstEnd: words[0].end,
    first: words[0].text,
    verb,
  };
}

function lowercased(word: string): string {
  const lower = word.toLowerCase();
  return word === "I" || word.startsWith("I'") || word.startsWith("I’")
    ? word
    : lower;
}

/* ------------------------------------------------------------------ rules */

interface Ctx {
  text: string;
  quote: Quote;
  lineText: string;
  lineEnd: number;
  inner: { char: string; index: number } | null;
  tag: Tag | null;
  after: string;
}

type Maker = (
  rule: DialogueRule,
  start: number,
  end: number,
  fixed: string | null,
  firm: boolean
) => DialogueFinding;

function ruleAfterClose(
  ctx: Ctx,
  make: Maker,
  placement: boolean
): DialogueFinding | null {
  const { text, quote, inner, tag, after } = ctx;
  if (!inner || quote.close === -1) {
    return null;
  }
  const closeMark = text[quote.close];
  const questionOrExclaim = inner.char === "?" || inner.char === "!";
  // "Why?", she asked — no comma after ? or !, inside or out.
  if (questionOrExclaim && after === ",") {
    return make(
      "comma-after-question",
      inner.index,
      quote.close + 2,
      `${inner.char}${closeMark}`,
      true
    );
  }
  if (
    inner.char === "," &&
    text[inner.index - 1] !== undefined &&
    "?!".includes(text[inner.index - 1])
  ) {
    return make(
      "comma-after-question",
      inner.index - 1,
      quote.close + 1,
      `${text[inner.index - 1]}${closeMark}`,
      true
    );
  }
  // "I'm leaving." she said — a period cannot stand before a tag.
  if (
    inner.char === "." &&
    tag?.verb &&
    !text.slice(inner.index - 2, inner.index).includes(".")
  ) {
    return periodBeforeTag(ctx, make);
  }
  if (
    placement &&
    (after === "." || after === ",") &&
    !RE_ELLIPSIS_AFTER.test(text.slice(quote.close + 1, quote.close + 3)) &&
    !questionOrExclaim
  ) {
    return make(
      "mark-placement",
      quote.close,
      quote.close + 2,
      `${after}${closeMark}`,
      true
    );
  }
  return null;
}

function periodBeforeTag(ctx: Ctx, make: Maker): DialogueFinding | null {
  const { text, inner, tag } = ctx;
  if (!(inner && tag)) {
    return null;
  }
  const written = text.slice(inner.index, tag.firstEnd);
  const fixed = `,${text.slice(inner.index + 1, tag.firstStart)}${lowercasedIfOpener(tag.first)}`;
  return make(
    "period-before-tag",
    inner.index,
    tag.firstEnd,
    fixed === written ? null : fixed,
    true
  );
}

function lowercasedIfOpener(word: string): string {
  return TAG_OPENERS.has(word.toLowerCase()) ? lowercased(word) : word;
}

/** "Go!" He said. / "Go," He said. — a tag after , ? or ! stays lowercase. */
function ruleCapitalTag(ctx: Ctx, make: Maker): DialogueFinding | null {
  const { text, inner, tag } = ctx;
  if (!(inner && tag?.verb && "?!,".includes(inner.char))) {
    return null;
  }
  const capitalised =
    RE_UPPER.test(tag.first) &&
    TAG_OPENERS.has(tag.first.toLowerCase()) &&
    tag.first !== "I";
  if (!capitalised) {
    return null;
  }
  const fixed = text.slice(inner.index, tag.firstStart) + lowercased(tag.first);
  return make("capital-tag", inner.index, tag.firstEnd, fixed, true);
}

/** "I'm leaving," she grabbed her coat. — an action takes a period, not a comma. */
function ruleActionBeat(ctx: Ctx, make: Maker): DialogueFinding | null {
  const { text, quote, inner, tag, lineText } = ctx;
  if (!(inner && tag) || inner.char !== "," || tag.verb) {
    return null;
  }
  // Only speech: a quoted phrase inside a sentence ("When they say "AMA," they mean…") needs its comma.
  if (!opensSpeech(text, quote, ctx.lineEnd - lineText.length)) {
    return null;
  }
  const subject = tag.first.toLowerCase();
  if (!SUBJECT_PRONOUNS.has(subject) || subject === "i" || subject === "you") {
    return null;
  }
  const following = wordsIn(
    text,
    tag.firstEnd,
    Math.min(ctx.lineEnd, tag.firstEnd + 30)
  )[0];
  if (
    !(following && isFiniteVerb(following.lower)) ||
    SPEECH_VERBS.has(following.lower)
  ) {
    return null;
  }
  const capital = tag.first.charAt(0).toUpperCase() + tag.first.slice(1);
  const middle = text.slice(inner.index + 1, tag.firstStart);
  return make(
    "action-beat",
    inner.index,
    tag.firstEnd,
    `.${middle}${capital}`,
    false
  );
}

const RE_COMMA_OR_COLON_END = /[,:]\s*$/;
const RE_COMMA_END = /,\s*$/;
const RE_PUNCTUATION_END = /[,:;—–.!?]\s*$/;
const RE_SPACE_END = /\s$/;

/** The quote starts its paragraph, or follows a speech verb and a comma or colon. */
function opensSpeech(text: string, quote: Quote, lineStart: number): boolean {
  const before = text.slice(lineStart, quote.open);
  return (
    before.trim().length === 0 ||
    (RE_COMMA_OR_COLON_END.test(before) && lastWordIsSpeechVerb(before))
  );
}

/** Says, "i'm leaving" — speech that opens a sentence starts with a capital. */
function ruleLowercaseStart(
  ctx: Ctx,
  previous: Quote | undefined,
  make: Maker
): DialogueFinding | null {
  const { text, quote, lineText } = ctx;
  const first = text[quote.open + 1];
  if (!(first && RE_LOWER.test(first))) {
    return null;
  }
  const lineStart = ctx.lineEnd - lineText.length;
  const before = text.slice(lineStart, quote.open);
  const atStart = before.trim().length === 0;
  const afterTag =
    RE_COMMA_OR_COLON_END.test(before) && lastWordIsSpeechVerb(before);
  if (
    !(atStart || afterTag) ||
    isContinuation(text, previous, quote, lineStart)
  ) {
    return null;
  }
  return make(
    "lowercase-start",
    quote.open,
    quote.open + 2,
    `${text[quote.open]}${first.toUpperCase()}`,
    false
  );
}

function lastWordIsSpeechVerb(before: string): boolean {
  const words = wordsIn(before);
  const last = words.at(-1);
  return last ? SPEECH_VERBS.has(last.lower) : false;
}

/** A quote after a tag and a comma, when the earlier quote ended with a comma: one broken sentence. */
function isContinuation(
  text: string,
  previous: Quote | undefined,
  quote: Quote,
  lineStart: number
): boolean {
  if (
    !previous ||
    previous.close === -1 ||
    previous.line !== quote.line ||
    previous.close < lineStart
  ) {
    return false;
  }
  const inner = lastInner(text, previous);
  const gap = text.slice(previous.close + 1, quote.open);
  // The comma may sit inside the first quote or, misplaced, just outside it.
  const broken =
    (inner !== null && ",—–-".includes(inner.char)) || gap.startsWith(",");
  return (
    broken &&
    RE_COMMA_END.test(gap) &&
    wordsIn(gap).some((word) => SPEECH_VERBS.has(word.lower))
  );
}

function ruleCapitalContinuation(
  ctx: Ctx,
  previous: Quote | undefined,
  make: Maker
): DialogueFinding | null {
  const { text, quote, lineText } = ctx;
  const first = text[quote.open + 1];
  if (!(first && RE_UPPER.test(first) && previous)) {
    return null;
  }
  const lineStart = ctx.lineEnd - lineText.length;
  if (!isContinuation(text, previous, quote, lineStart)) {
    return null;
  }
  const word = wordsIn(
    text,
    quote.open + 1,
    Math.min(ctx.lineEnd, quote.open + 20)
  )[0];
  if (!(word && CONTINUATION_WORDS.has(word.lower)) || word.text === "I") {
    return null;
  }
  return make(
    "capital-continuation",
    quote.open,
    quote.open + 2,
    `${text[quote.open]}${first.toLowerCase()}`,
    false
  );
}

/** Verbs that only ever introduce speech, so a noun subject ("The teacher asked") is enough. */
const SPEECH_ONLY: ReadonlySet<string> = new Set([
  "asked",
  "replied",
  "answered",
  "whispered",
  "shouted",
  "yelled",
  "cried",
  "muttered",
  "murmured",
  "exclaimed",
  "snapped",
  "demanded",
]);

/**
 * "She said", "Tom asked" introduce speech. "A sheet that says "Chicago"" quotes a label, so
 * a noun subject counts only with a verb that is about speaking.
 */
function speaksDirectly(words: readonly Word[], verb: string): boolean {
  if (verb === "called" || verb === "told") {
    return false;
  }
  let at = words.length - 2;
  while (at >= 0 && RE_LY.test(words[at].lower)) {
    at--;
  }
  const subject = words[at];
  if (!subject) {
    return true;
  }
  const lower = subject.lower;
  if (SUBJECT_PRONOUNS.has(lower) && lower !== "it") {
    return true;
  }
  return RE_UPPER.test(subject.text) || SPEECH_ONLY.has(verb);
}

/** She said "I'm leaving." — a tag before the quote takes a comma. */
function ruleMissingComma(ctx: Ctx, make: Maker): DialogueFinding | null {
  const { text, quote, lineText } = ctx;
  const lineStart = ctx.lineEnd - lineText.length;
  const before = text.slice(lineStart, quote.open);
  if (RE_PUNCTUATION_END.test(before) || !RE_SPACE_END.test(before)) {
    return null;
  }
  const words = wordsIn(before);
  const last = words.at(-1);
  const opens = text[quote.open + 1] ?? "";
  // A quoted word ("She shouted "fire!" and ran") is not speech that needs a comma.
  if (!(last && SPEECH_VERBS.has(last.lower)) || RE_LOWER.test(opens)) {
    return null;
  }
  if (!speaksDirectly(words, last.lower)) {
    return null;
  }
  const verbStart = lineStart + last.start;
  return make(
    "missing-comma-after-tag",
    verbStart,
    quote.open + 1,
    `${last.text}, ${text[quote.open]}`,
    true
  );
}

/* ------------------------------------------------------------------ report */

const RULES: readonly DialogueRule[] = [
  "period-before-tag",
  "missing-comma-after-tag",
  "comma-after-question",
  "mark-placement",
  "capital-tag",
  "action-beat",
  "lowercase-start",
  "capital-continuation",
  "unclosed",
];

export interface DialogueOptions {
  /** Check that periods and commas sit inside the closing mark. Default true. */
  checkPlacement?: boolean;
}

export function checkDialoguePunctuation(
  text: string,
  options: DialogueOptions = {}
): DialogueReport {
  const placement = options.checkPlacement !== false;
  const { quotes, lines } = allQuotes(text);
  const findings: DialogueFinding[] = [];
  let paired = 0;
  for (const [index, quote] of quotes.entries()) {
    const line = lines[quote.line - 1];
    const lineText = text.slice(line.start, line.end);
    const make: Maker = (rule, start, end, fixed, firm) => ({
      rule,
      start,
      end,
      written: text.slice(start, end),
      fixed,
      firm,
      line: quote.line,
      lineText,
    });
    if (quote.close === -1) {
      const next = lines[quote.line];
      const continues = next
        ? text[next.start] === '"' || text[next.start] === OPENING
        : false;
      if (!continues) {
        findings.push(
          make(
            "unclosed",
            quote.open,
            Math.min(line.end, quote.open + 20),
            null,
            false
          )
        );
      }
      continue;
    }
    paired++;
    const previous = quotes[index - 1];
    const ctx: Ctx = {
      text,
      quote,
      lineText,
      lineEnd: line.end,
      inner: lastInner(text, quote),
      tag: tagAfter(text, quote, line.end),
      after: text[quote.close + 1] ?? "",
    };
    for (const finding of [
      ruleAfterClose(ctx, make, placement),
      ruleCapitalTag(ctx, make),
      ruleActionBeat(ctx, make),
      ruleLowercaseStart(ctx, previous, make),
      ruleCapitalContinuation(ctx, previous, make),
      ruleMissingComma(ctx, make),
    ]) {
      if (finding) {
        findings.push(finding);
      }
    }
  }
  findings.sort((a, b) => a.start - b.start);
  const byRule = Object.fromEntries(RULES.map((rule) => [rule, 0])) as Record<
    DialogueRule,
    number
  >;
  for (const finding of findings) {
    byRule[finding.rule]++;
  }
  return {
    words: countWords(text),
    lines: lines.length,
    quotes: paired,
    findings,
    byRule,
  };
}
